import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";

// ─────────────────────────────────────────────────────────────────────────────
// Doctor KYC — per-doctor identity / credential documents.
// Backend: /api/v1/doctors/{doctor_id}/kyc (private S3 bucket — reads go through
// a short-lived presigned GET URL, never a permanent URL).
//
// Admin verify/reject endpoints exist on the backend but have no UI here yet —
// this card is the doctor's own self-service upload, so only the read/upload
// calls are wired.
// ─────────────────────────────────────────────────────────────────────────────

export type KycDocType = "aadhar" | "pan" | "medical_registration" | "degree";
export type KycStatus = "submitted" | "verified" | "rejected";

/** A current (non-superseded) KYC document. */
export interface KycDocument {
  kyc_doc_id:       string;
  doctor_id:        string;
  document_type:    KycDocType;
  status:           KycStatus;
  rejection_reason: string | null;
  verified_at:      string | null;
  verified_by:      string | null;
  created_at:       string;
  updated_at:       string;
}

export interface KycPresignedUrlEntry {
  document_type: KycDocType;
  upload_url:    string | null;   // null = S3 disabled (dev): skip the PUT
  s3_key:        string;
}

export interface KycPresignedUrlSet {
  entries:    KycPresignedUrlEntry[];
  s3_enabled: boolean;
}

export interface KycConfirmItem {
  document_type: KycDocType;
  s3_key:        string;
}

export const DoctorKycAPI = {
  /** Current docs (one per type). */
  list(doctorId: string): Promise<KycDocument[]> {
    return apiRequest<KycDocument[]>(ROUTES.DOCTOR_KYC(doctorId));
  },

  /** Issue S3 PUT URLs for the given doc types. */
  presign(doctorId: string, documentTypes: KycDocType[]): Promise<KycPresignedUrlSet> {
    return apiRequest<KycPresignedUrlSet>(ROUTES.DOCTOR_KYC_PRESIGN(doctorId), {
      method: "POST",
      body: JSON.stringify({ document_types: documentTypes }),
    });
  },

  /** Record uploaded files — inserts a new current row per type. */
  confirm(doctorId: string, documents: KycConfirmItem[]): Promise<KycDocument[]> {
    return apiRequest<KycDocument[]>(ROUTES.DOCTOR_KYC(doctorId), {
      method: "POST",
      body: JSON.stringify({ documents }),
    });
  },

  /** Short-lived presigned GET URL for viewing a stored document. */
  viewUrl(doctorId: string, kycDocId: string): Promise<{ view_url: string }> {
    return apiRequest<{ view_url: string }>(ROUTES.DOCTOR_KYC_VIEW_URL(doctorId, kycDocId));
  },
};

// ── Platform admin API (PlatformAdmin role only) ──────────────────────────────

export interface PendingKycItem {
  kyc_doc_id:   string;
  doctor_id:    string;
  doctor_name:  string;
  clinic_name:  string;
  doc_type:     KycDocType;
  status:       KycStatus;
  submitted_at: string;
  view_url:     string | null;  // presigned GET URL (null if S3 disabled in dev)
}

export const PlatformKycAPI = {
  /** All submitted (pending) KYC docs across all clinics. PlatformAdmin only. */
  listPending(): Promise<PendingKycItem[]> {
    return apiRequest<PendingKycItem[]>("/platform/kyc/pending");
  },

  verify(doctorId: string, kycDocId: string): Promise<KycDocument> {
    return apiRequest<KycDocument>(`/platform/kyc/${doctorId}/${kycDocId}/verify`, {
      method: "POST",
    });
  },

  reject(doctorId: string, kycDocId: string, rejection_reason: string): Promise<KycDocument> {
    return apiRequest<KycDocument>(`/platform/kyc/${doctorId}/${kycDocId}/reject`, {
      method: "POST",
      body: JSON.stringify({ rejection_reason }),
    });
  },
};
